import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listAudit, listMembers, listPages } from "../api/reports";
import { ApiError } from "../api/client";
import type { AccessAuditEvent, TenantBranding, TenantMember, TenantPage } from "../types";
import { PageHeader } from "../components/Chrome";

export function AdminHome({
  branding,
  tenantSlug,
}: {
  branding: TenantBranding;
  tenantSlug: string;
}) {
  const [members, setMembers] = useState<TenantMember[] | null>(null);
  const [pages, setPages] = useState<TenantPage[] | null>(null);
  const [audit, setAudit] = useState<AccessAuditEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const [m, p, a] = await Promise.all([
          listMembers(tenantSlug),
          listPages(tenantSlug),
          listAudit(tenantSlug, 10),
        ]);
        setMembers(m);
        setPages(p);
        setAudit(a);
      } catch (e) {
        setError(e instanceof ApiError ? e.detail : (e as Error).message);
      }
    })();
  }, [tenantSlug]);

  return (
    <div className="container">
      <PageHeader eyebrow={branding.display_name} title="Operator console" />
      {error && <div className="empty err">{error}</div>}
      <div className="kpi-row">
        <KPI label="People" value={members?.length ?? "—"} to="/admin/people" />
        <KPI label="Pages" value={pages?.length ?? "—"} to="/admin/pages" />
        <KPI
          label="Brand color"
          value={branding.primary_color}
          to="/admin/branding"
          color={branding.primary_color}
        />
      </div>
      <h2 className="section-title">Recent activity</h2>
      {audit === null && <div className="empty">Loading…</div>}
      {audit && audit.length === 0 && <div className="empty">No activity yet.</div>}
      {audit && audit.length > 0 && (
        <ul className="audit-list">
          {audit.map((e) => (
            <li key={e.id} className="audit-row">
              <span className="audit-actor">{e.actor_email}</span>
              <span className="audit-verb">{verbFor(e.action)}</span>
              <span className="audit-target">
                {e.target_member_email ?? "—"}
                {e.target_page_name ? ` · ${e.target_page_name}` : ""}
                {e.target_feature_name ? ` › ${e.target_feature_name}` : ""}
              </span>
              <span className="audit-time">{e.occurred_at}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function KPI({
  label,
  value,
  to,
  color,
}: {
  label: string;
  value: number | string;
  to: string;
  color?: string;
}) {
  return (
    <Link to={to} className="kpi">
      <span className="kpi-label">{label}</span>
      <span className="kpi-value" style={color ? { color } : undefined}>
        {value}
      </span>
    </Link>
  );
}

function verbFor(action: string): string {
  switch (action) {
    case "grant_page":
      return "granted";
    case "revoke_page":
      return "revoked";
    case "grant_feature":
      return "enabled";
    case "revoke_feature":
      return "hid";
    default:
      return action;
  }
}
